/**
 * One shared cell-key convention so every grid, the presence badges and the
 * server locks all agree on what "the same cell" means.
 *
 *   cellKey('orders', 'line', 123456, 'lineStatus') -> "orders:line:123456:lineStatus"
 */

const part = (value) => String(value ?? '').trim().replace(/:/g, '-') || '-';

export const cellKey = (page, entityType, entityId, field) =>
    [page, entityType, entityId, field].map(part).join(':');

/** Parse a key back into its pieces (used by the roster tooltips). */
export const parseCellKey = (key) => {
    const [page, entityType, entityId, field] = String(key || '').split(':');
    return { page, entityType, entityId, field };
};

/** Human label for "what is this person working on", shown in presence tooltips. */
export const PRESENCE_FIELD_LABELS = {
    lineStatus: 'Line status',
    orderStatus: 'Order status',
    quoteStatus: 'Quote status',
    lineTag: 'Line tag',
    comment: 'Comments',
    orderAccess: 'Order access links',
    folderUrl: 'Folder URL',
    review: 'Review',
    ready: 'Ready',
    confirm: 'Confirm & move',
};

/**
 * Merge several cells' rosters into one avatar list for a grid row, so a row
 * shows who is on it AND who is editing one of its cells - the editors have to
 * be visible without anyone expanding the order.
 *
 * Earlier groups win, so pass the most specific activity first: someone both on
 * the row and in its status picker is described as being in the picker.
 *
 * @param {Array<{users: Array, activity: string}>} groups
 * @returns {Array} users, each tagged with the activity that placed them there
 */
export const mergeRowPresence = (groups) => {
    const byEmail = new Map();
    (groups || []).forEach(({ users, activity }) => {
        (users || []).forEach((user) => {
            if (!user || byEmail.has(user.email)) return;
            byEmail.set(user.email, { ...user, activity });
        });
    });
    return [...byEmail.values()];
};

export const describeCell = (key) => {
    const { entityType, entityId, field } = parseCellKey(key);
    const label = PRESENCE_FIELD_LABELS[field] || field || 'a cell';
    if (!entityId || entityId === '-') return label;
    return `${label} · ${entityType === 'line' ? 'line' : ''} ${entityId}`.replace(/\s+/g, ' ').trim();
};
